// Convert HEIC/HEIF (the iPhone default capture format) to JPEG in the browser
// BEFORE upload. Canvas-based compressors (compressorjs, browser-image-
// compression) can't decode HEIC, and the backend's image validation rejects
// it unless server-side HEIF decoding is available — so an unconverted HEIC
// photo silently disappears from the order. heic2any bundles libheif (wasm,
// ~1.4 MB), so it is loaded lazily via dynamic import ONLY when a HEIC file is
// actually detected, keeping it out of the main bundle.

const isHeic = (file) => {
  const type = (file?.type || '').toLowerCase();
  const name = (file?.name || '').toLowerCase();
  return (
    type === 'image/heic' ||
    type === 'image/heif' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  );
};

// Returns a JPEG File for a HEIC input, or the original file unchanged for
// anything else (or if conversion fails — best-effort, never blocks upload).
export const convertHeicIfNeeded = async (file) => {
  if (!(file instanceof File) || !isHeic(file)) {
    return file;
  }

  try {
    const {default: heic2any} = await import('heic2any');
    const result = await heic2any({blob: file, toType: 'image/jpeg', quality: 0.9});
    const blob = Array.isArray(result) ? result[0] : result;
    const newName = file.name.replace(/\.(heic|heif)$/i, '') + '.jpg';
    return new File([blob], newName, {
      type: 'image/jpeg',
      lastModified: file.lastModified || Date.now(),
    });
  } catch (error) {
    // Keep the original so the upload still proceeds (the backend tolerant
    // field will drop it if truly undecodable, never 400 the whole order).
    console.error('HEIC->JPEG conversion failed; using original file', error);
    return file;
  }
};

// Convert a FileList / array, preserving order.
export const convertHeicList = async (files = []) =>
  Promise.all(Array.from(files).map(convertHeicIfNeeded));
